import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatRupiah, formatDateTimeJakarta, STATUS_META, OCR_META } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Wallet, Clock, AlertTriangle, FileSpreadsheet, ArrowRight } from "lucide-react";

function Metric({ icon: Icon, label, value, sub, testid, tone = "" }) {
  return (
    <Card data-testid={testid} className="p-5 border-border">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
        <Icon className={`w-4 h-4 ${tone}`} />
      </div>
      <p className="mt-3 text-2xl font-bold font-mono tracking-tight">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </Card>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard/summary")).data,
    refetchInterval: 8000,
  });

  if (isLoading || !data)
    return <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {[0,1,2,3].map(i => <div key={i} className="h-28 rounded-xl bg-secondary animate-pulse" />)}
    </div>;

  return (
    <div className="space-y-6 nh-fade">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Ringkasan</h1>
        <p className="text-sm text-muted-foreground">Ikhtisar belanja operasional hari ini · {data.today} WIB</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric testid="metric-today" icon={Wallet} label="Belanja Disetujui Hari Ini"
          value={formatRupiah(data.today_total)} sub={`${data.approved} nota disetujui total`} />
        <Metric testid="metric-pending" icon={Clock} tone="text-amber-500" label="Menunggu Pemeriksaan"
          value={data.pending} sub="perlu tindakan admin" />
        <Metric testid="metric-variance" icon={AlertTriangle} tone="text-orange-500" label="Nota Berselisih"
          value={data.variance_count} sub="total hitung ≠ total nota" />
        <Metric testid="metric-dr" icon={FileSpreadsheet} tone={data.daily_report_connected ? "text-emerald-500" : "text-muted-foreground"}
          label="Baris Daily Report" value={data.daily_report_entries}
          sub={data.daily_report_connected ? "template terhubung" : "belum terhubung"} />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2">
          <h2 className="font-semibold mb-4">Belanja Disetujui per Outlet</h2>
          {data.per_outlet.length === 0 ? (
            <p className="text-sm text-muted-foreground py-10 text-center">Belum ada data disetujui.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.per_outlet}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="outlet" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))"
                  tickFormatter={(v) => "Rp " + (v / 1000) + "k"} />
                <Tooltip formatter={(v) => formatRupiah(v)}
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="total" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} maxBarSize={64} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-5">
          <h2 className="font-semibold mb-4">Aktivitas Terbaru</h2>
          <div className="space-y-3">
            {data.recent.length === 0 && <p className="text-sm text-muted-foreground">Belum ada nota.</p>}
            {data.recent.map((r) => (
              <button key={r.id} onClick={() => nav(`/review/${r.id}`)}
                className="w-full text-left flex items-center gap-3 p-2 rounded-lg hover:bg-secondary transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{r.supplier || "Nota tanpa nama supplier"}</p>
                  <p className="text-xs text-muted-foreground truncate">{r.outlet_name} · {formatDateTimeJakarta(r.uploaded_at)}</p>
                </div>
                <Badge variant="outline" className={`text-[10px] ${STATUS_META[r.status]?.cls}`}>
                  {STATUS_META[r.status]?.label}
                </Badge>
                <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
